import type { AxiosInstance } from "axios";
import type { z } from "zod";
import type {
  ConfigureCrmBody,
  ConfigureCrmResult,
} from "@shared/schemas/configure-crm.schemas";
import type { EventUpdateableInternalSchema } from "@shared/schemas/event.schemas";
import type {
  CreateEventRequest,
  EventBrandingField,
  UpdateEventRequest,
} from "@shared/schemas/event-branding-upload.schemas";
import { EventBrandingUploadResultSchema } from "@shared/schemas/event-branding-upload.schemas";
import type { Paginated } from "@shared/schemas/paginated.schemas";
import {
  EventBrandingUploadError,
  EVENT_BRANDING_UPLOAD_ERROR_MESSAGES,
} from "@frontend/shared/features/events/domain/errors/event-branding-upload.errors";

type EventUpdateableInternalInput = z.input<
  typeof EventUpdateableInternalSchema
>;

const EVENTS_API_PATH = "/api/v1/events";
const ME_EVENTS_API_PATH = "/api/v1/users/me/events";

export function createEventsApi(apiClient: AxiosInstance) {
  return {
    async getByIdPublicOrSlug(idPublicOrSlug: string): Promise<unknown> {
      const response = await apiClient.get(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublicOrSlug)}`,
      );
      return response.data;
    },

    async getPublicView(idPublicOrSlug: string): Promise<unknown> {
      const response = await apiClient.get(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublicOrSlug)}/public`,
      );
      return response.data;
    },

    async getEventJoinInfo(
      idPublicOrSlug: string,
      options: {
        readonly inv?: string | undefined;
        readonly lang?: string | undefined;
      } = {},
    ): Promise<unknown> {
      const params: Record<string, string> = {};
      if (options.inv !== undefined) params["inv"] = options.inv;
      if (options.lang !== undefined) params["lang"] = options.lang;
      const response = await apiClient.get(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublicOrSlug)}/join-info`,
        { params },
      );
      return response.data;
    },

    async getMyEvents(): Promise<readonly unknown[]> {
      const response =
        await apiClient.get<readonly unknown[]>(ME_EVENTS_API_PATH);
      return response.data;
    },

    async getAllEvents(): Promise<readonly unknown[]> {
      const response = await apiClient.get<Paginated<unknown>>(EVENTS_API_PATH);
      return response.data.items;
    },

    async create(data: CreateEventRequest): Promise<unknown> {
      const response = await apiClient.post(EVENTS_API_PATH, data);
      return response.data;
    },

    async stageBrandingUpload(input: {
      readonly eventIdPublic?: string | undefined;
      readonly field: EventBrandingField;
      readonly file: File;
    }): Promise<{ readonly uploadKey: string; readonly etag: string }> {
      const { eventIdPublic, field, file } = input;
      const route =
        eventIdPublic === undefined
          ? `${EVENTS_API_PATH}/branding/upload-url`
          : `${EVENTS_API_PATH}/${encodeURIComponent(eventIdPublic)}/branding/upload-url`;
      const response = await apiClient.post<unknown>(route, {
        field,
        mimeType: file.type,
      });
      const upload = EventBrandingUploadResultSchema.parse(response.data);
      const putResponse = await apiClient.put<void>(upload.uploadUrl, file, {
        headers: { "Content-Type": file.type },
      });
      const etag = String(putResponse.headers["etag"] ?? "");
      if (etag.length === 0) {
        throw new EventBrandingUploadError(
          EVENT_BRANDING_UPLOAD_ERROR_MESSAGES.MISSING_ETAG,
        );
      }
      return { uploadKey: upload.uploadKey, etag };
    },

    async update(idPublic: string, data: UpdateEventRequest): Promise<unknown> {
      const response = await apiClient.post(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublic)}`,
        data,
      );
      return response.data;
    },

    async updateInternal(
      idPublic: string,
      data: EventUpdateableInternalInput,
    ): Promise<unknown> {
      const response = await apiClient.patch(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublic)}/internal`,
        data,
      );
      return response.data;
    },

    async configureCrm(
      idPublic: string,
      data: ConfigureCrmBody,
    ): Promise<ConfigureCrmResult> {
      const response = await apiClient.patch<ConfigureCrmResult>(
        `${EVENTS_API_PATH}/${encodeURIComponent(idPublic)}/crm`,
        data,
      );
      return response.data;
    },
  };
}
